import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Download, Printer, FileText, Award, CheckCircle, ChevronRight,
  Brain, Target, Calendar, BarChart3, BookOpen, TrendingUp, Zap,
  Dumbbell, Apple, Moon, Shield, Clock, Crosshair, Compass, 
  LineChart, PenTool, Star, Heart, Activity
} from 'lucide-react';

const GolferBenefitsPage = () => {
  const location = useLocation();
  const isFromDashboard = location.pathname.includes('/dashboard/');

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C5530] text-white py-6 print:hidden">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to={isFromDashboard ? "/dashboard" : "/"} className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {isFromDashboard ? 'Back to Dashboard' : 'Back to Home'}
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors rounded" data-testid="print-btn">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-white/90 text-[#2C5530] font-semibold transition-colors rounded" data-testid="download-btn">
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 print:py-0 print:px-0 print:max-w-none">
        
        {/* Title */}
        <div className="text-center mb-16 print:mb-8">
          <div className="flex justify-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" alt="Quiet Mind Golf" className="h-20 w-auto" />
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1A1A1A] mb-4 print:text-3xl">Your Mental Game Dashboard</h1>
          <p className="text-xl text-[#57534E] max-w-2xl mx-auto print:text-base">
            Everything you need to master your mind, build confidence, and perform freely under pressure — all in one place.
          </p>
        </div>

        {/* Executive Summary */}
        <section className="bg-white border border-[#E7E5E4] p-8 mb-12 print:border-0 print:shadow-none print:mb-6" data-testid="executive-summary">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4 flex items-center gap-3">
            <FileText className="w-6 h-6 text-[#2C5530]" /> What You Get
          </h2>
          <p className="text-[#57534E] leading-relaxed mb-4">
            Quiet Mind Golf gives you a personal performance dashboard built around the QMG Blueprint — 
            Mindful, Accepting, Commitment. Log your practice and tournament sessions, track your game stats, 
            journal your thoughts, record performance scorecards, and monitor the four Pillars of Performance. 
            Over time, the data reveals patterns you can't see in the moment, building the self-awareness that 
            translates to confidence on the first tee.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">QMG</p>
              <p className="text-sm text-[#57534E]">Blueprint Framework</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">4</p>
              <p className="text-sm text-[#57534E]">Performance Pillars</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">5</p>
              <p className="text-sm text-[#57534E]">Tracking Tools</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#BC562B]">Pro</p>
              <p className="text-sm text-[#57534E]">Unlimited Access</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">Free</p>
              <p className="text-sm text-[#57534E]">Get Started Today</p>
            </div>
          </div>
        </section>

        {/* QMG Blueprint */}
        <section className="mb-12 print:mb-6" data-testid="mac-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Brain className="w-6 h-6 text-[#2C5530]" /> The QMG Blueprint
          </h2>
          <div className="bg-[#2C5530] p-8 text-white mb-6">
            <p className="text-white/90 leading-relaxed mb-6">
              At the heart of Quiet Mind Golf is a proven, research-backed framework for performing under pressure. 
              The QMG Blueprint — Awareness, Acceptance, Action — is woven into every session log, diary entry, 
              and scorecard. It's not about eliminating nerves. It's about learning to perform freely despite them.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 p-5">
                <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Step Back</p>
                <h3 className="font-['Playfair_Display'] text-xl mb-2 text-white">Mindful</h3>
                <p className="text-white/80 text-sm">Recognise when you're in an unhelpful mental state. Make the conscious decision to not let the moment own you.</p>
              </div>
              <div className="bg-white/10 p-5">
                <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Step Up</p>
                <h3 className="font-['Playfair_Display'] text-xl mb-2 text-white">Accepting</h3>
                <p className="text-white/80 text-sm">Accept your thoughts and feelings without judgement. See your situation as a challenge, not a threat.</p>
              </div>
              <div className="bg-white/10 p-5">
                <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Step In</p>
                <h3 className="font-['Playfair_Display'] text-xl mb-2 text-white">Commitment</h3>
                <p className="text-white/80 text-sm">Commit fully to your process and personal values. Execute with attention on your swing keys and "feels."</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-[#737373] italic">
            Backed by 20+ years of peer-reviewed research. <Link to="/mac-research" className="text-[#2C5530] hover:underline print:hidden">Read the science</Link>
          </p>
        </section>

        {/* Key Features */}
        <section className="mb-12 print:mb-6" data-testid="features-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Award className="w-6 h-6 text-[#2C5530]" /> What's Inside Your Dashboard
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Calendar, title: 'Session Logging', desc: 'Log practice and tournament sessions with detailed focus areas, mood tracking (before & after), key learnings, and QMG application. Capture what you worked on, how you felt, and what you took away.' },
              { icon: Target, title: 'Game Stats Tracking', desc: 'Record your rounds with score, putts, fairways hit, greens in regulation, and a mental score out of 10. Track trends over time to see the connection between your mental game and your results.' },
              { icon: BookOpen, title: 'Personal Diary', desc: 'A private space to journal your thoughts, reflect on rounds, and process emotions. Tag each entry with which parts of the QMG Blueprint you applied that day.' },
              { icon: Crosshair, title: 'Performance Scorecards', desc: 'Score yourself across 16 mental performance criteria — Before, During, In-Between, and After your round. Track which areas are strong and where to focus next.' },
              { icon: BarChart3, title: 'Reports & Insights', desc: 'Visual reports with charts and trends across your sessions, game stats, scorecards, and pillars. See your progress over any date range you choose.' },
              { icon: Activity, title: 'Shotgun Pattern Analysis', desc: 'Track where your shots land relative to your target. Identify patterns in your misses to focus your practice where it matters most.' },
              { icon: Compass, title: 'Dashboard Overview', desc: 'Your personal command centre. See total sessions, games recorded, diary entries, average mental score, and quick actions — all at a glance.' },
              { icon: Star, title: 'Tasks & Goals', desc: 'Receive tasks from your coach, set personal goals, and track your commitments. Stay accountable to the process that drives improvement.' },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C5530] text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">{f.title}</h3>
                    <p className="text-[#57534E] text-sm">{f.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Performance Scorecards Detail */}
        <section className="mb-12 print:mb-6 print:break-before-page" data-testid="scorecards-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Crosshair className="w-6 h-6 text-[#2C5530]" /> Performance Scorecards — In Detail
          </h2>
          <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
            <p className="text-[#57534E] mb-6">
              Rate yourself from 1-10 across 16 criteria that cover the full arc of a competitive round. 
              Over time, your scorecard data reveals which phase of competition needs the most attention.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#2C5530]" /> Before the Round
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5 ml-6">
                  <li>Preparation & warm-up quality</li>
                  <li>Mental readiness & focus</li>
                  <li>Commitment to your game plan</li>
                  <li>Confidence level on the first tee</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#BC562B]" /> During the Round
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5 ml-6">
                  <li>Shot-by-shot commitment</li>
                  <li>Decision making quality</li>
                  <li>Emotional control under pressure</li>
                  <li>Staying in the present moment</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#7C3AED]" /> In-Between Shots
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5 ml-6">
                  <li>Recovery from bad shots</li>
                  <li>Walking routine & body language</li>
                  <li>Letting go of outcomes</li>
                  <li>Maintaining positive energy</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#059669]" /> After the Round
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5 ml-6">
                  <li>Post-round reflection quality</li>
                  <li>Honest self-assessment</li>
                  <li>Identifying areas for growth</li>
                  <li>Setting next-round intentions</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars of Performance */}
        <section className="mb-12 print:mb-6" data-testid="pillars-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Dumbbell className="w-6 h-6 text-[#2C5530]" /> Pillars of Performance
          </h2>
          <p className="text-[#57534E] mb-6">
            Great golf isn't just about what happens on the course. Track the four pillars that support 
            sustained performance — because your body and mind off the course directly affect your game on it.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Brain, title: 'Mindset', desc: 'Meditation, visualisation, breath work, journaling', bg: 'bg-purple-50', text: 'text-purple-600', label: 'text-purple-900', sub: 'text-purple-700' },
              { icon: Dumbbell, title: 'Movement', desc: 'S&C, yoga, mobility, cardio, stretching', bg: 'bg-blue-50', text: 'text-blue-600', label: 'text-blue-900', sub: 'text-blue-700' },
              { icon: Apple, title: 'Nutrition', desc: 'Daily meals, hydration, game day fuelling', bg: 'bg-green-50', text: 'text-green-600', label: 'text-green-900', sub: 'text-green-700' },
              { icon: Moon, title: 'Recovery', desc: 'Sleep quality, ice bath, massage, rest days', bg: 'bg-red-50', text: 'text-red-600', label: 'text-red-900', sub: 'text-red-700' },
            ].map((p, i) => (
              <div key={i} className={`p-5 ${p.bg} rounded text-center`}>
                <p.icon className={`w-10 h-10 ${p.text} mx-auto mb-3`} />
                <p className={`font-semibold ${p.label} text-lg mb-1`}>{p.title}</p>
                <p className={`text-xs ${p.sub}`}>{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-white border border-[#E7E5E4] p-5 print:border-gray-300">
            <p className="text-sm text-[#57534E]">
              Log activities under each pillar with duration and notes. Your dashboard shows weekly breakdowns, 
              pie charts by category, and trends over time — helping you build consistency in the habits that matter.
            </p>
          </div>
        </section>

        {/* Session Types */}
        <section className="mb-12 print:mb-6 print:break-before-page" data-testid="sessions-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Calendar className="w-6 h-6 text-[#2C5530]" /> Session Types
          </h2>
          <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
            <p className="text-[#57534E] mb-6">
              Log three types of coaching sessions, each capturing different aspects of your development:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#7C3AED]" /> Mental Coaching
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5">
                  {['Visualisation & imagery', 'Breathing techniques', 'Self-talk management', 'Focus & concentration', 'Pre-shot routine work', 'Mood before & after'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#2563EB]" /> Technical Coaching
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5">
                  {['Putting & short game', 'Driving & iron play', 'Wedge work', 'Focus areas with quality ratings', 'Key learnings', 'Coach task follow-up'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#059669]" /> On-Course Coaching
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1.5">
                  {['Course management', 'Decision making under pressure', 'Recovery shot strategies', 'Tournament round review', 'Game plan execution', 'Real-time performance logging'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Reports */}
        <section className="mb-12 print:mb-6" data-testid="reports-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <LineChart className="w-6 h-6 text-[#2C5530]" /> Reports & Insights
          </h2>
          <p className="text-[#57534E] mb-6">
            Your data tells a story. The reports section turns your logs into visual insights 
            that reveal patterns, progress, and areas for growth.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, title: 'Performance Summary', items: ['Score & mental score trends', 'Mood before vs after analysis', 'Total sessions & rounds', 'Overall progression'] },
              { icon: Activity, title: 'Practice Sessions', items: ['Session type breakdown', 'Focus area frequency', 'Duration trends', 'Full session log with mood'] },
              { icon: Target, title: 'Game Stats', items: ['Score progression chart', 'Putts, FIR, GIR tracking', 'Best/worst round analysis', 'Mental vs actual score'] },
              { icon: Crosshair, title: 'Scorecard Analytics', items: ['Before/During/After radar charts', 'Category averages', 'Score trends over time', 'Strengths & growth areas'] },
              { icon: Dumbbell, title: 'Pillars of Performance', items: ['Activity breakdown by pillar', 'Weekly stacked bar chart', 'Minutes tracked per category', 'Consistency patterns'] },
              { icon: Activity, title: 'Shotgun Patterns', items: ['Visual shot distribution', 'Miss direction analysis', 'Club-by-club patterns', 'Focus your practice'] },
            ].map((r, i) => (
              <div key={i} className="bg-white border border-[#E7E5E4] p-5 print:border-gray-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-[#2C5530]/10 text-[#2C5530] flex items-center justify-center rounded">
                    <r.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A]">{r.title}</h3>
                </div>
                <ul className="text-sm text-[#57534E] space-y-1">
                  {r.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Diary */}
        <section className="mb-12 print:mb-6" data-testid="diary-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <PenTool className="w-6 h-6 text-[#2C5530]" /> Personal Diary
          </h2>
          <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
            <p className="text-[#57534E] mb-4">
              Your private space to reflect, process, and grow. The diary is where the real mental 
              work happens — honest self-reflection that builds the awareness the QMG Blueprint depends on.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3">What You Can Log</h4>
                <ul className="text-sm text-[#57534E] space-y-1.5">
                  {['Free-form text entries', 'Mood rating with each entry', 'Tag as Practice, Tournament, or Reflection', 'QMG Blueprint application checkboxes', 'Date and time stamped'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3">Why It Matters</h4>
                <ul className="text-sm text-[#57534E] space-y-1.5">
                  {['Builds self-awareness over time', 'Identifies recurring thought patterns', 'Tracks emotional responses to pressure', 'Creates a record of growth you can revisit', 'Strengthens the mind-body connection'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><Heart className="w-3.5 h-3.5 text-[#BC562B] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Free vs Pro */}
        <section className="mb-12 print:mb-6 print:break-before-page" data-testid="plans-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#2C5530]" /> Free vs Pro
          </h2>
          <div className="bg-white border border-[#E7E5E4] overflow-hidden print:border-gray-300">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F2F0E9]">
                  <th className="text-left p-4 font-medium text-[#57534E]">Feature</th>
                  <th className="text-center p-4 font-medium text-[#57534E] w-24">Free</th>
                  <th className="text-center p-4 font-semibold text-[#BC562B] w-24">Pro</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Session logs', free: '5/month', pro: 'Unlimited' },
                  { feature: 'Data history', free: '3 months', pro: 'Unlimited' },
                  { feature: 'Game stats tracking', free: true, pro: true },
                  { feature: 'Personal diary', free: true, pro: true },
                  { feature: 'Performance scorecards', free: false, pro: true },
                  { feature: 'Pillars of Performance', free: false, pro: true },
                  { feature: 'Shotgun pattern analysis', free: false, pro: true },
                  { feature: 'Full reports & insights', free: false, pro: true },
                  { feature: 'Scorecard analytics', free: false, pro: true },
                  { feature: 'Coach connection', free: true, pro: true },
                ].map((row, i) => (
                  <tr key={i} className="border-t border-[#E7E5E4]">
                    <td className="p-4 text-[#1A1A1A]">{row.feature}</td>
                    <td className="p-4 text-center">
                      {typeof row.free === 'string' ? (
                        <span className="text-[#737373]">{row.free}</span>
                      ) : row.free ? (
                        <CheckCircle className="w-4 h-4 text-[#2C5530] mx-auto" />
                      ) : (
                        <span className="text-[#D4D4D4]">—</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {typeof row.pro === 'string' ? (
                        <span className="text-[#BC562B] font-medium">{row.pro}</span>
                      ) : row.pro ? (
                        <CheckCircle className="w-4 h-4 text-[#BC562B] mx-auto" />
                      ) : (
                        <span className="text-[#D4D4D4]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-4 bg-[#F2F0E9] text-center">
              <p className="text-sm text-[#57534E]">Pro: <strong className="text-[#1A1A1A]">$99/year</strong> — that's less than the cost of a single lesson for a full year of mental game tracking.</p>
            </div>
          </div>
        </section>

        {/* Getting Started */}
        <section className="mb-12 print:mb-6" data-testid="getting-started-section">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Zap className="w-6 h-6 text-[#2C5530]" /> Getting Started
          </h2>
          <div className="bg-[#F2F0E9] p-8 rounded-lg">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Create Your Account', desc: 'Sign up free in under a minute. No credit card required. You can upgrade to Pro at any time.' },
                { step: '2', title: 'Start Logging', desc: 'Log your first session, record a round, or write a diary entry. The dashboard starts building your picture immediately.' },
                { step: '3', title: 'Watch Your Growth', desc: 'Over days and weeks, your data reveals patterns. Use the insights to focus your practice and build real confidence.' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-[#2C5530] text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold">{s.step}</span>
                  </div>
                  <h4 className="font-semibold text-[#1A1A1A] mb-2">{s.title}</h4>
                  <p className="text-sm text-[#57534E]">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 print:hidden" data-testid="cta-section">
          <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-4">Ready to Master Your Mental Game?</h2>
          <p className="text-[#57534E] mb-8 max-w-xl mx-auto">
            Join golfers who are building the self-awareness and confidence that comes from tracking what matters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-[#BC562B] text-white px-8 py-4 font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-colors" data-testid="register-cta">
              Start Free Account <ChevronRight className="w-5 h-5" />
            </Link>
            <Link to="/the-approach" className="inline-flex items-center justify-center gap-2 border border-[#2C5530] text-[#2C5530] px-8 py-4 font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-colors" data-testid="approach-cta">
              Explore The Mind Factor <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Print Footer */}
        <div className="hidden print:block mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-500">
          <p>Quiet Mind Golf - Golfer Dashboard Benefits</p>
          <p>&copy; {new Date().getFullYear()} Quiet Mind Golf. All rights reserved.</p>
          <p className="mt-2">www.quietmindgolf.co.uk</p>
        </div>
      </main>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:break-before-page { break-before: page; }
        }
      `}</style>
    </div>
  );
};

export default GolferBenefitsPage;
